import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/UI';

function Section({ title, children }) {
  return (
    <div className="mb-8">
      <h2 className="font-serif font-semibold text-green-900 text-lg mb-3 pb-2 border-b border-amber-100">{title}</h2>
      <div className="text-gray-600 text-sm leading-relaxed space-y-3">{children}</div>
    </div>
  );
}

export default function TermsPage() {
  const updated = 'March 8, 2026';

  return (
    <div className="min-h-screen flex flex-col">
      {/* Nav */}
      <nav className="bg-white border-b border-amber-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-serif font-bold text-green-900 text-lg">GolfBoard Scores</span>
          </Link>
          <Link to="/login" className="btn-primary text-sm">Sign In</Link>
        </div>
      </nav>

      <main className="flex-1 page-container py-10">
        <div className="max-w-2xl mx-auto">
          <h1 className="font-serif font-bold text-3xl text-green-900 mb-1">Terms & Conditions</h1>
          <p className="text-sm text-gray-400 mb-10">Last updated: {updated}</p>

          <Section title="1. Acceptance of Terms">
            <p>By accessing or using GolfBoard Scores ("the Service," "we," "our," or "us"), you agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use the Service.</p>
            <p>We reserve the right to update or modify these Terms at any time without prior notice. Your continued use of the Service following any changes constitutes your acceptance of the revised Terms.</p>
          </Section>

          <Section title="2. Description of Service">
            <p>GolfBoard Scores is a free, community-driven platform that allows amateur golfers to self-report golf scores and compare them against other users on leaderboards organized by golf course and tee. The Service is provided for recreational and community purposes only.</p>
            <p>GolfBoard Scores is not affiliated with, endorsed by, or connected to any professional golf association, governing body, or official handicap system.</p>
          </Section>

          <Section title="3. User Accounts">
            <p>To access certain features of the Service, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information as necessary. You may also create an account using Google Sign-In, in which case your Google account email will be used as your account email.</p>
            <p>You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account, regardless of how it was created. You agree to notify us immediately of any unauthorized use of your account.</p>
            <p>Usernames must be 3–20 characters and may only contain letters, numbers, and underscores. Usernames that contain profanity, hate speech, or impersonate another person or entity are prohibited and may result in account termination.</p>
          </Section>

          <Section title="4. Score Integrity & Honor System">
            <p>All scores posted on GolfBoard Scores are self-reported and unverified. By posting a score, you represent that it is a genuine, accurate reflection of a round of golf you completed.</p>
            <p>GolfBoard Scores operates on an honor system consistent with the traditions of the game of golf. Deliberately falsifying scores, posting fictitious rounds, or manipulating leaderboard standings is a violation of these Terms and the spirit of the community.</p>
            <p>We reserve the right to remove scores or suspend accounts that we reasonably believe to be fraudulent or in violation of these Terms, though we are under no obligation to monitor or verify any scores submitted.</p>
          </Section>

          <Section title="5. User Conduct">
            <p>You agree not to use the Service to:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Post false, misleading, or fraudulent information</li>
              <li>Harass, threaten, or intimidate other users</li>
              <li>Upload or transmit any content that is unlawful, defamatory, obscene, or otherwise objectionable</li>
              <li>Impersonate any person or entity</li>
              <li>Use the Service for any commercial purpose without our prior written consent</li>
              <li>Attempt to gain unauthorized access to any portion of the Service or its related systems</li>
            </ul>
          </Section>

          <Section title="6. Prohibition on Web Scraping & Automated Access">
            <p>You may not access, scrape, crawl, index, or otherwise collect data from the Service through automated means, including but not limited to bots, spiders, scrapers, data mining tools, or any other automated extraction methods, without our express prior written permission.</p>
            <p>Any attempt to systematically extract data from GolfBoard Scores — including user profiles, scores, course information, or leaderboard data — is strictly prohibited. We reserve the right to block IP addresses, terminate accounts, and pursue all available legal remedies against parties that violate this provision.</p>
            <p>Accessing the Service via the API in a manner that exceeds reasonable personal use, or in a way designed to replicate or compete with GolfBoard Scores, is also prohibited.</p>
          </Section>

          <Section title="7. Intellectual Property">
            <p>The GolfBoard Scores name, logo, design, and all original content created by us are the intellectual property of GolfBoard Scores and may not be reproduced, distributed, or used without express written permission.</p>
            <p>You retain ownership of any content you submit (such as your username and scores), but by submitting content to the Service you grant us a non-exclusive, royalty-free license to display and use that content in connection with operating the Service.</p>
          </Section>

          <Section title="8. Privacy">
            <p>Your use of the Service is also governed by our Privacy Policy. By using GolfBoard Scores, you consent to the collection and use of your information as described therein. We do not sell personal data to third parties.</p>
            <p>Please be aware that usernames and scores posted to leaderboards are publicly visible to all users of the Service.</p>
          </Section>

          <Section title="9. Disclaimers">
            <p>The Service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the Service will be uninterrupted, error-free, or free of viruses or other harmful components.</p>
            <p>Leaderboard rankings and scores are user-submitted and unverified. GolfBoard Scores makes no representations regarding the accuracy, completeness, or reliability of any scores or rankings on the platform.</p>
          </Section>

          <Section title="10. Limitation of Liability">
            <p>To the fullest extent permitted by law, GolfBoard Scores and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Service, even if we have been advised of the possibility of such damages.</p>
          </Section>

          <Section title="11. Termination">
            <p>We reserve the right to suspend or terminate your account at any time, with or without notice, for conduct that we believe violates these Terms or is harmful to other users, the Service, or third parties.</p>
            <p>You may delete your account at any time by contacting us. Upon termination, your right to use the Service will immediately cease.</p>
          </Section>

          <Section title="12. Governing Law">
            <p>These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be resolved through binding arbitration or in the courts of competent jurisdiction.</p>
          </Section>

          <Section title="13. Contact">
            <p>If you have questions about these Terms and Conditions, please reach out to us through the GolfBoard Scores platform. We're a small community project and genuinely appreciate your feedback.</p>
          </Section>

          <div className="mt-10 p-4 bg-green-50 rounded-xl border border-green-100 text-sm text-green-800">
            <strong>TL;DR:</strong> Play fair, post real scores, don't scrape our data, be respectful to other golfers, and enjoy the game. GolfBoard Scores is a community built on the same integrity the sport has always valued.
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-green-900 text-green-300 py-8 px-5 mt-10">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <Logo size="sm" />
            <span className="font-serif font-semibold text-amber-200">GolfBoard Scores</span>
          </div>
          <div className="flex gap-5">
            <Link to="/" className="hover:text-amber-200 transition-colors">Home</Link>
            <Link to="/login" className="hover:text-amber-200 transition-colors">Sign In</Link>
          </div>
          <p className="text-green-500 text-xs">© {new Date().getFullYear()} GolfBoard Scores. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}